import { cn } from "@/lib/utils";
import { useState } from "react";

/**
 * Tints are picked by hashing the name so the same person keeps the same colour
 * across renders and screens. Each pair is a light ground with a darker text of
 * the same hue, kept low-saturation so a column of them doesn't shout over the
 * content beside it.
 */
const PERSON_TINTS = [
  { background: "#E2EDE5", foreground: "#3F6B4E" },
  { background: "#E8E5F2", foreground: "#574A87" },
  { background: "#F3E6DE", foreground: "#8A5233" },
  { background: "#E1E8F0", foreground: "#3B5878" },
  { background: "#F1EBDC", foreground: "#6F5A2E" },
  { background: "#DFEBEB", foreground: "#35696A" },
] as const;

function tintFor(seed: string) {
  let hash = 0;
  for (let index = 0; index < seed.length; index++) {
    hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  }
  return PERSON_TINTS[Math.abs(hash) % PERSON_TINTS.length];
}

function initialsFor(name: string | null | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function InitialsAvatar(props: {
  name: string | null | undefined;
  className?: string;
}) {
  const initials = initialsFor(props.name);
  const tint = tintFor(props.name ?? "");

  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full text-2xs font-medium tracking-[0.02em]",
        props.className,
      )}
      style={{ backgroundColor: tint.background, color: tint.foreground }}
    >
      {initials}
    </span>
  );
}

/**
 * The organization's own logo when it has one, and initials otherwise. Either
 * way it's a rounded square rather than a tinted circle, so the workspace mark
 * never reads as just another person in a list.
 */
export function OrgAvatar(props: {
  name: string | null | undefined;
  logoUrl?: string | null;
  className?: string;
}) {
  // A logo URL that 404s would otherwise leave a broken-image glyph in the
  // header, so a failed load falls back to the initials.
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = !!props.logoUrl && !logoFailed;

  if (showLogo) {
    return (
      <img
        src={props.logoUrl ?? undefined}
        alt=""
        aria-hidden
        onError={() => setLogoFailed(true)}
        className={cn(
          "size-6 shrink-0 rounded-[7px] object-contain object-center",
          props.className,
        )}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-[7px] bg-neutral-700 text-2xs font-semibold tracking-[0.02em] text-white",
        props.className,
      )}
    >
      {initialsFor(props.name)}
    </span>
  );
}
